import { getAuthData } from '@common/helpers/auth';
import Config from '@skin_config';
import { openDeferModal } from '@tickets/helpers/openDeferPopup';
import BISService from '@common/services/bis';
import { POPUPS } from '@common/constants/popups';
import { createBISResponseModel, createBISUserModel, createPlatformUserModel } from '@common/services/bis/models';
import { swarmRequest } from '@common/helpers/util/requestMiddleware';
import { getPopupDispatchers } from '../contexts/popup';
import { store } from '../index/terminalApp';
import { SetVerificationResponse } from '../actions/user';
import { getTimestampToken } from './timestampToken';
import { omit } from 'lodash';

const checkToken = (cb) => (
  async (...args) => {
    try {
      if (BISService.token || !Config.verification.externalValidation) return cb(...args);

      const token = await getTimestampToken();
      BISService.authorize(token);

      return cb(...args);
    } catch {
      return createBISResponseModel(null, false, [{ message: 'Failed to get timestamp token, please try again' }]);
    }
  }
);

const verificationHandler = checkToken(async (form) => {
  const { token } = getAuthData();

  if (Config.verification.findPlatformPlayer) {
    const { data } = await swarmRequest(
      { token, filter: { document_number: form.identifier } },
      'terminal_find_player',
    );

    const player = data?.find(item => item.DocNumber === form.identifier)

    if (!player) {
      return createBISResponseModel(null, false, [{ message: 'User not found ' }]);
    }

    return createBISResponseModel(createPlatformUserModel(player, Config.verification));
  }

  if (!Config.verification.externalValidation) return createBISResponseModel(createBISUserModel(form, Config.verification));

  const response = await BISService.validate(Config, Config.verification.partner, {
    ...omit(form, ['tickets']),
    cashdeskId: getAuthData().tid,
    amount: 0,
    customerId: 0,
  }, Config.verification);

  if (!response.success){
    if (+form.identifier !== form.tickets?.[0]?.dataFromSwarm?.member_id)
      return createBISResponseModel(response.data?.customer, response.success, response.errors);

    return createBISResponseModel(createBISUserModel(form, Config.verification));
  }

  return createBISResponseModel(response.data?.customer, response.success, response.errors);
});

export const verifyUser = async (isOptional, tickets) => {
  const result = await openDeferModal(POPUPS.VERIFICATION, {
    config: Config,
    isOptional,
    tickets,
    onVerify: verificationHandler,
  }, true, { stack: true });

  return result ? createBISResponseModel(result) : null;
};

export const applyUser = async () => {
  const result = await verifyUser(!Config.verification.required);

  if (result?.success && !result.data.skipped) {
    const { openPopup } = getPopupDispatchers();

    store.dispatch(SetVerificationResponse(result));

    openPopup(POPUPS.INFO, {
      title: 'Document was successfully applied',
      closePopupSeconds: 2,
      icon: 'global-icon icon-success-empty',
    }, { stack: true });
  }

  return result;
};

export const logoutUser = () => store.dispatch(SetVerificationResponse(null));

export const verifyUserAmount = checkToken(async (user, amount) => {
  const { tid } = getAuthData();
  const {
    externalValidation, amountValidation, findPlatformPlayer, partner,
  } = Config.verification;

  if (!externalValidation || !amountValidation || !findPlatformPlayer) return createBISResponseModel();

  const result = await BISService.validate(Config, partner, {
    amount,
    cashdeskId: tid,
    customerId: user.id,
  });

  return createBISResponseModel(null, result.success, result.errors);
});

export const createUserBet = checkToken(async (user, bet) => {
  if (!Config.verification.externalValidation || !Config.verification.betCreation) return createBISResponseModel();

  const result = await BISService.createBet(Config, Config.verification.partner, {
    ticketId: bet.id.toString(),
    betAmount: bet.amount,
    betCreateDate: bet.created,
    cashdeskId: getAuthData().tid,
    customerId: user.id,
  });

  return createBISResponseModel(null, result.success, result.errors);
});

export const payUserBet = checkToken(async (user, bet) => {
  if (!Config.verification.externalValidation || !Config.verification.betPayment || !user?.id) return createBISResponseModel();

  const result = await BISService.payBet(Config, Config.verification.partner, {
    ticketId: bet.id.toString(),
    betAmount: bet.amount,
    betCreateDate: bet.created,
    cashdeskId: getAuthData().tid,
    customerId: user.id,
  });

  return createBISResponseModel(null, result.success, result.errors);
});

export const askForUserLogout = () => {
  const { openPopup } = getPopupDispatchers();

  openPopup(POPUPS.VERIFICATION_LOGOUT, {
    onLogout: logoutUser,
  }, { stack: true });
};
